// frontend/redux/slices/adminOfferSlice.js
// Admin side: list / create / update / delete offers.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API = () => `${import.meta.env.VITE_BACKEND_URL}/api/offers`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` } });
const msg = (e, fallback) => e.response?.data?.message || e.message || fallback;

export const fetchAdminOffers = createAsyncThunk("adminOffers/fetch", async (_, { rejectWithValue }) => {
  try {
    return (await axios.get(API(), auth())).data;
  } catch (e) {
    return rejectWithValue(msg(e, "Failed to load offers"));
  }
});

// id present -> update, otherwise create
export const saveOffer = createAsyncThunk("adminOffers/save", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = id ? await axios.put(`${API()}/${id}`, data, auth()) : await axios.post(API(), data, auth());
    return res.data;
  } catch (e) {
    return rejectWithValue(msg(e, "Failed to save offer"));
  }
});

export const deleteOffer = createAsyncThunk("adminOffers/delete", async (id, { rejectWithValue }) => {
  try {
    await axios.delete(`${API()}/${id}`, auth());
    return id;
  } catch (e) {
    return rejectWithValue(msg(e, "Failed to delete offer"));
  }
});

const adminOfferSlice = createSlice({
  name: "adminOffers",
  initialState: { offers: [], loading: false, saving: false, error: null },
  reducers: {
    clearOfferError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminOffers.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchAdminOffers.fulfilled, (s, a) => {
        s.loading = false;
        s.offers = a.payload;
      })
      .addCase(fetchAdminOffers.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      .addCase(saveOffer.pending, (s) => {
        s.saving = true;
        s.error = null;
      })
      .addCase(saveOffer.fulfilled, (s) => {
        s.saving = false; // list is re-fetched by the page (other offers may have changed too)
      })
      .addCase(saveOffer.rejected, (s, a) => {
        s.saving = false;
        s.error = a.payload;
      })
      .addCase(deleteOffer.fulfilled, (s, a) => {
        s.offers = s.offers.filter((o) => o._id !== a.payload);
      })
      .addCase(deleteOffer.rejected, (s, a) => {
        s.error = a.payload;
      });
  },
});

export const { clearOfferError } = adminOfferSlice.actions;
export default adminOfferSlice.reducer;
